import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePointsName, type GeneralSettingsDto, type RefillBonusTierDto } from '@gc/shared';

import { api } from '@/lib/api';
import { Field, PageHeading, SaveBar, Section, inputClass } from './controls';
import { LoyaltySection, type LoyaltyRateKey } from './LoyaltySection';
import { useGeneralForm, type GeneralSettingsPatch } from './useGeneralForm';
import { useUnsavedGuard } from './useUnsavedGuard';
import { toFieldErrors, useResource } from './useSettings';

/** The fields THIS page writes — Confirm and the guard share it. */
function loyaltyPayload(row: GeneralSettingsDto): GeneralSettingsPatch {
  return {
    pointsName: row.pointsName,
    pointsPerThousandIqd: row.pointsPerThousandIqd,
    pointsPerHourPlayed: row.pointsPerHourPlayed,
    refillBonusFlatPercent: row.refillBonusFlatPercent,
  };
}

/**
 * Loyalty & rewards — what the venue calls its points, how fast they are
 * earned, and the refill bonus (wave 3, #19).
 *
 * As on Printing, the two halves save differently and that is on purpose: the
 * rates are columns of the venue row and wait for Confirm, while a tier is its
 * own record and is added, toggled or deleted IMMEDIATELY. A tier edit that
 * waited for Confirm would look identical to one that had already applied.
 */
export function LoyaltyPage() {
  const { t } = useTranslation();
  const { data, form, set, loading, error, errorFor, setErrors, saving, saved, save } =
    useGeneralForm();

  const tiers = useResource<RefillBonusTierDto[]>('/settings/refill-bonus-tiers');

  // Tier CRUD saves itself, so only the rates above can be unsaved.
  useUnsavedGuard(
    !!form &&
      !!data &&
      JSON.stringify(loyaltyPayload(form)) !== JSON.stringify(loyaltyPayload(data)),
  );

  if (loading && !form)
    return <p className="p-6 text-sm text-content-muted">{t('common.loading')}</p>;
  if (error) return <p className="p-6 text-sm text-danger">{error}</p>;
  if (!form) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form) return;

    await save(loyaltyPayload(form), t('settings.general.saveFailed'));
  }

  /* ---- Refill-bonus tier CRUD (immediate, like printers) ---------------- */

  async function addTier(minAmountIqd: number, bonusPercent: number): Promise<void> {
    setErrors([]);
    try {
      await api.post('/settings/refill-bonus-tiers', { minAmountIqd, bonusPercent });
      await tiers.reload();
    } catch (caught) {
      // A duplicate threshold answers tier_exists with a minAmountIqd detail,
      // which lands under the tier amount input via errorFor.
      setErrors(toFieldErrors(caught, t('settings.general.tierAddFailed')));
    }
  }

  async function toggleTier(tier: RefillBonusTierDto, isActive: boolean): Promise<void> {
    setErrors([]);
    try {
      await api.patch(`/settings/refill-bonus-tiers/${tier.id}`, { isActive });
      await tiers.reload();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.general.tierUpdateFailed')));
    }
  }

  async function deleteTier(tier: RefillBonusTierDto): Promise<void> {
    setErrors([]);
    try {
      await api.delete(`/settings/refill-bonus-tiers/${tier.id}`);
      await tiers.reload();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.general.tierDeleteFailed')));
    }
  }

  function setRate(key: LoyaltyRateKey, value: number): void {
    set(key, value);
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="p-6">
      <PageHeading
        title={t('settings.general.loyaltyTitle')}
        description={t('settings.general.loyaltyDescription')}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* The name comes first because every preview line under the rates is
            written in it — reading them in the wrong word is confusing. */}
        <Section id="set-points-name" title={t('settings.general.pointsNameSection')}>
          <Field
            label={t('settings.general.pointsName')}
            hint={t('settings.general.pointsNameHint', {
              fallback: t('settings.general.pointsNameFallback'),
            })}
            error={errorFor('pointsName')}
          >
            <input
              value={form.pointsName ?? ''}
              onChange={(event) => set('pointsName', event.target.value || null)}
              placeholder={t('settings.general.pointsNameFallback')}
              className={inputClass}
            />
          </Field>
        </Section>

        <LoyaltySection
          id="set-loyalty-rates"
          pointsPerHourPlayed={form.pointsPerHourPlayed}
          pointsPerThousandIqd={form.pointsPerThousandIqd}
          refillBonusFlatPercent={form.refillBonusFlatPercent}
          pointsName={resolvePointsName(form.pointsName, t('settings.general.pointsNameFallback'))}
          currencySymbol={form.currencySymbol}
          tiers={tiers.data}
          tiersError={tiers.error}
          tierBusy={tiers.loading}
          onRateChange={setRate}
          onAddTier={(minAmountIqd, bonusPercent) => void addTier(minAmountIqd, bonusPercent)}
          onDeleteTier={(tier) => void deleteTier(tier)}
          onToggleTier={(tier, isActive) => void toggleTier(tier, isActive)}
          errorFor={errorFor}
        />
      </div>

      <SaveBar saving={saving} saved={saved} error={errorFor('(root)')} />
    </form>
  );
}
